import { useCallback, useMemo } from 'react';
import { useClientApi, useClientUpdateApi } from '@hero/api-hooks';
import { getObjectId } from '@hero/schema';
import { usePublishOrderStore } from '@hero/hooks-shared';
import {
  mergeClientSettingsWithLoad,
  projectLoadIntoClientSettings,
} from './clientLoadDefaults';

/**
 * Phase 2 (relations-v2) — companion to {@link useApplyClientOrderDefaultsManager}
 * that lets the user persist the publish-page edits as the new default
 * for the picked Client.
 *
 * Available whenever a CRM Client is picked and the user has changed
 * any load default — no creator-role gate. Carriers publishing on
 * behalf of a client get the same save affordance.
 *
 * Returns the data needed to render the save-defaults banner:
 *   - `targetName`     — the picked Client's name (drives the button label).
 *   - `canSaveForClient` — `true` when the user has changed any default
 *                          and a CRM Client is currently selected.
 *   - `saveForClient` — async action that PATCHes
 *                       `Client.settings` from the current load draft
 *                       (merged with existing settings so contacts /
 *                       invoicing flags survive).
 *   - `isSaving`       — mutation in flight.
 *   - `isClientLoading` — client doc fetch in flight (for UI spinner).
 */
export interface UseSaveOrderDefaultsManagerReturn {
  targetName?: string;
  canSaveForClient: boolean;
  saveForClient: () => Promise<void>;
  isSaving: boolean;
  isClientLoading: boolean;
}

export function useSaveOrderDefaultsManager(): UseSaveOrderDefaultsManagerReturn {
  const customer = usePublishOrderStore(s => s.customer);
  const isInternalShipment = usePublishOrderStore(s => s.isInternalShipment);
  const load = usePublishOrderStore(s => s.load);
  const loadOverrides = usePublishOrderStore(s => s.loadOverrides);

  const clientId = !isInternalShipment ? customer?.customerId : undefined;
  const { data: client, isLoading: isClientLoading } = useClientApi(clientId);
  const updateClient = useClientUpdateApi();

  const hasOverrides = useMemo(() => Object.keys(loadOverrides).length > 0, [loadOverrides]);

  const canSaveForClient = !isInternalShipment && Boolean(clientId) && hasOverrides;

  const saveForClient = useCallback(async () => {
    if (!canSaveForClient || !clientId) return;
    const loadPatch = projectLoadIntoClientSettings({
      vehicleTypes: load.vehicleTypes ?? (load.vehicleType ? [load.vehicleType] : undefined),
      itemDescription: load.itemDescription,
      itemType: load.itemType,
      packing: load.packing,
      quantity: load.quantity,
      weightTon: load.weightTon,
      itemCost: load.itemCost,
      temperatureType: load.temperatureType,
      temperatureDegree: load.temperatureDegree,
      podSettings: load.podSettings,
    });
    const settings = mergeClientSettingsWithLoad(client?.settings, loadPatch);
    await updateClient.mutateAsync({
      _id: getObjectId(clientId),
      settings,
    });
  }, [canSaveForClient, client?.settings, clientId, load, updateClient]);

  return {
    targetName: customer?.name ?? client?.name,
    canSaveForClient,
    saveForClient,
    isSaving: updateClient.isPending,
    isClientLoading: Boolean(clientId) && isClientLoading,
  };
}
